import { useState, useCallback, useRef, useEffect } from 'react';
import { Box } from '@mui/material';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import type { SxProps, Theme } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import { IconButton, Tooltip } from '@occc-app/design-system';
import { Drive_Response_Reaction } from '@occc/graphql';

type ReactionButtonsProps = {
  responseId: string;
  content: string;
  currentReaction?: Drive_Response_Reaction | null;
  onReactionChange: (responseId: string, reaction: Drive_Response_Reaction | null) => void;
  disabled?: boolean;
};

const containerSx: SxProps<Theme> = {
  display: 'flex',
  gap: 0.5,
  mt: 1.5,
};

const iconSx = {
  fontSize: 16,
};

const getButtonSx = (theme: Theme): SxProps<Theme> => ({
  width: 32,
  height: 32,
  color: 'primary.main',
  '&:hover': {
    bgcolor: alpha(theme.palette.primary.main, 0.1),
  },
});

export const ReactionButtons = ({
  responseId,
  content,
  currentReaction,
  onReactionChange,
  disabled,
}: ReactionButtonsProps) => {
  const theme = useTheme();
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isGood = currentReaction === Drive_Response_Reaction.Good;
  const isBad = currentReaction === Drive_Response_Reaction.Bad;

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
        copyTimeoutRef.current = null;
      }
    };
  }, []);

  const handleGoodClick = useCallback(() => {
    const newReaction = isGood ? null : Drive_Response_Reaction.Good;
    onReactionChange(responseId, newReaction);
  }, [isGood, onReactionChange, responseId]);

  const handleBadClick = useCallback(() => {
    const newReaction = isBad ? null : Drive_Response_Reaction.Bad;
    onReactionChange(responseId, newReaction);
  }, [isBad, onReactionChange, responseId]);

  const handleCopyClick = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
      copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API failed silently
    }
  }, [content]);

  return (
    <Box sx={containerSx}>
      <Tooltip title={copied ? 'Copied!' : 'Copy response'}>
        <IconButton
          onClick={handleCopyClick}
          disabled={disabled}
          sx={getButtonSx(theme)}
          aria-label='Copy response to clipboard'
        >
          {copied ? <CheckIcon sx={iconSx} /> : <ContentCopyIcon sx={iconSx} />}
        </IconButton>
      </Tooltip>
      <Tooltip title={isGood ? 'Remove reaction' : 'Good response'}>
        <IconButton
          onClick={handleGoodClick}
          disabled={disabled}
          sx={getButtonSx(theme)}
          aria-label='Mark as good response'
        >
          {isGood ? <ThumbUpIcon sx={iconSx} /> : <ThumbUpOutlinedIcon sx={iconSx} />}
        </IconButton>
      </Tooltip>
      <Tooltip title={isBad ? 'Remove reaction' : 'Bad response'}>
        <IconButton
          onClick={handleBadClick}
          disabled={disabled}
          sx={getButtonSx(theme)}
          aria-label='Mark as bad response'
        >
          {isBad ? <ThumbDownIcon sx={iconSx} /> : <ThumbDownOutlinedIcon sx={iconSx} />}
        </IconButton>
      </Tooltip>
    </Box>
  );
};
